import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useGuardStore } from "../context/GuardContext";
import axios from "axios";
import { toast } from "react-toastify";

const BASE_URL = process.env.REACT_APP_BASE_URL;

export default function GuardDutyDecision() {
  const navigate = useNavigate();
  const { selectedGuard } = useGuardStore();

  const [guardId, setGuardId] = useState(null);
  const [guardData, setGuardData] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [decisionData, setDecisionData] = useState({
    status: "",
    reason: "",
  });

  const [existingDecision, setExistingDecision] = useState(null);
  const [checkingDecision, setCheckingDecision] = useState(true);

  /* ================= LOAD GUARD FROM CONTEXT OR LOCALSTORAGE ================= */
  useEffect(() => {
    let guard = selectedGuard;

    if (!guard) {
      const storedGuard = localStorage.getItem("selectedGuard");
      if (storedGuard) {
        guard = JSON.parse(storedGuard);
      }
    }

    if (!guard) {
      toast.error("Session expired. Please login again.");
      navigate("/login");
      return;
    }

    setGuardId(guard.id);
    setGuardData(guard);
  }, [selectedGuard, navigate]);

  /* ================= FETCH EXISTING DECISION ================= */
  useEffect(() => {
    const fetchExistingDecision = async () => {
      try {
        const token = localStorage.getItem("guardToken");
        if (!token) {
          toast.error("Token expired. Please login again.");
          navigate("/login");
          return;
        }

        const res = await axios.get(`${BASE_URL}/api/duty/${guardId}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (res.data && res.data.length > 0) {
          setExistingDecision(res.data[0]);
        } else {
          setExistingDecision(null);
        }
      } catch (err) {
        console.error("Fetch Decision Error:", err);
        setExistingDecision(null);
      } finally {
        setCheckingDecision(false);
      }
    };

    if (guardId) fetchExistingDecision();
  }, [guardId, navigate]);

  /* ================= STATUS HANDLER ================= */
  const handleStatusClick = (status) => {
    setDecisionData({
      status,
      reason: "",
    });
  };

  /* ================= REASON HANDLER ================= */
  const handleReasonChange = (e) => {
    setDecisionData((prev) => ({
      ...prev,
      reason: e.target.value,
    }));
  };

  /* ================= SUBMIT ================= */
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    if (!decisionData.status) {
      toast.error("Please select Accept or Reject");
      return;
    }

    if (
      decisionData.status === "Guard Rejected" &&
      !decisionData.reason.trim()
    ) {
      toast.error("Reason is required for rejection");
      return;
    }

    const dutyPayload = {
      officer: guardData,
      status: decisionData.status,
      reason: decisionData.reason,
      message: decisionData.reason,
    };

    try {
      setSubmitting(true);

      const token = localStorage.getItem("guardToken");
      if (!token) {
        toast.error("Token expired. Please login again.");
        navigate("/login");
        return;
      }

      await axios.post(`${BASE_URL}/api/duty/decision`, dutyPayload, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      toast.success("Duty decision submitted successfully");
      setDecisionData({ status: "", reason: "" });
      navigate("/guardshift");
    } catch (err) {
      console.error("Duty Submit Error:", err);
      toast.error("Failed to submit duty decision");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <div className="duty-decision-wrapper">
        <header className="decision-header">
          <h1>
            <i className="fas fa-clipboard-check"></i> Duty Decision
          </h1>
          <p>Review your assignment and confirm your availability</p>
        </header>

        <main className="decision-main">
          <div className="decision-grid">
            {/* Left: Officer Info */}
            <div className="officer-card">
              <div className="card-header">
                <i className="fas fa-user-shield"></i>
                <h2>Officer Information</h2>
              </div>
              <div className="officer-details">
                <p><strong>ID:</strong> {guardId || "Loading..."}</p>
                <p><strong>Name:</strong> {guardData?.name || "Loading..."}</p>
                <p><strong>Rank:</strong> {guardData?.rank || "N/A"}</p>
              </div>
              <button
                className="back-btn"
                onClick={() => navigate("/guardshift")}
              >
                ← Back to Shift Details
              </button>
            </div>

            {/* Right: Decision Form */}
            <div className="decision-card">
              {checkingDecision ? (
                <div className="loading-state">
                  <i className="fas fa-spinner fa-spin"></i>
                  <p>Checking previous decision...</p>
                </div>
              ) : existingDecision ? (
                <div className="existing-decision">
                  <div className="card-header success">
                    <i className="fas fa-check-circle"></i>
                    <h2>Decision Already Submitted</h2>
                  </div>
                  <div className="decision-info">
                    <p><strong>Status:</strong> 
                      <span className={`status-badge ${existingDecision.status.toLowerCase().replace(' ', '-')}`}>
                        {existingDecision.status}
                      </span>
                    </p>
                    {existingDecision.message && (
                      <p><strong>Reason:</strong> {existingDecision.message}</p>
                    )}
                    <p><strong>Officer:</strong> {existingDecision.officer?.name}</p>
                    <p><strong>Rank:</strong> {existingDecision.officer?.rank}</p>
                  </div>
                  <button
                    className="back-btn primary"
                    onClick={() => navigate("/guardshift")}
                  >
                    ← Return to Shift
                  </button>
                </div>
              ) : (
                <>
                  <div className="card-header">
                    <i className="fas fa-tasks"></i>
                    <h2>Submit Duty Decision</h2>
                  </div>

                  <form onSubmit={handleSubmit} className="decision-form">
                    <div className="status-buttons">
                      <button
                        type="button"
                        disabled={submitting}
                        onClick={() => handleStatusClick("Guard Accepted")}
                        className={`status-btn accept ${
                          decisionData.status === "Guard Accepted" ? "active" : ""
                        }`}
                      >
                        <i className="fas fa-check"></i>
                        Accept Duty
                      </button>

                      <button
                        type="button"
                        disabled={submitting}
                        onClick={() => handleStatusClick("Guard Rejected")}
                        className={`status-btn reject ${
                          decisionData.status === "Guard Rejected" ? "active" : ""
                        }`}
                      >
                        <i className="fas fa-times"></i>
                        Reject Duty
                      </button>
                    </div>

                    {decisionData.status === "Guard Rejected" && (
                      <div className="reason-section">
                        <label><strong>Reason for Rejection <span className="required">*</span></strong></label>
                        <textarea
                          value={decisionData.reason}
                          onChange={handleReasonChange}
                          rows="4"
                          placeholder="Please explain why you cannot accept this duty..."
                          required
                        />
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={submitting || !decisionData.status}
                      className="submit-btn"
                    >
                      {submitting ? (
                        <>
                          <i className="fas fa-spinner fa-spin"></i> Submitting...
                        </>
                      ) : (
                        "Submit Decision"
                      )}
                    </button>
                  </form>
                </>
              )}
            </div>
          </div>
        </main>
      </div>

      <style jsx>{`
        .duty-decision-wrapper {
          min-height: 100vh;
          background: linear-gradient(135deg, #e0e7ff 0%, #f8fbff 100%);
          color: #1e293b;
          padding: 2rem;
        }

        .decision-header {
          text-align: center;
          margin-bottom: 2.5rem;
          padding: 2rem;
          background: linear-gradient(135deg, #1e40af, #3b82f6);
          border-radius: 24px;
          color: white;
          box-shadow: 0 15px 40px rgba(30, 64, 175, 0.3);
        }

        .decision-header h1 {
          font-size: 2.6rem;
          font-weight: 800;
          margin: 0 0 1rem 0;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 1rem;
          color:#fff;
        }

        .decision-header p {
          font-size: 1.3rem;
          opacity: 0.95;
          margin: 0;
        }

        .decision-main {
          max-width: 1400px;
          margin: 0 auto;
        }

        .decision-grid {
          display: grid;
          grid-template-columns: 1fr 1.5fr;
          gap: 2.5rem;
        }

        .officer-card,
        .decision-card {
          background: rgba(255, 255, 255, 0.94);
          backdrop-filter: blur(16px);
          border-radius: 24px;
          padding: 2.5rem;
          box-shadow: 0 15px 40px rgba(59, 130, 246, 0.18);
          border: 1px solid rgba(59, 130, 246, 0.4);
          transition: all 0.4s ease;
          height: fit-content;
        }

        .officer-card:hover,
        .decision-card:hover {
          transform: translateY(-8px);
          box-shadow: 0 25px 50px rgba(59, 130, 246, 0.25);
        }

        .card-header {
          display: flex;
          align-items: center;
          gap: 1rem;
          margin-bottom: 2rem;
          color: #1e40af;
          font-size: 1.3rem;
        }

        .card-header i {
          font-size: 2rem;
          color: #3b82f6;
        }

        .card-header.success {
          color: #155724;
        }

        .card-header.success i {
          color: #28a745;
        }

        .card-header h2 {
          font-size: 1.8rem;
          font-weight: 700;
          margin: 0;
          color:#fff;
        }

        .officer-details p {
          font-size: 1.15rem;
          margin: 1rem 0;
          color: #334155;
        }

        .officer-details strong {
          color: #1e40af;
          font-weight: 700;
        }

        .status-buttons {
          display: flex;
          gap: 1.5rem;
          margin-bottom: 2rem;
        }

        .status-btn {
          flex: 1;
          padding: 1.4rem;
          border: none;
          border-radius: 16px;
          font-size: 1.2rem;
          font-weight: 700;
          cursor: pointer;
          transition: all 0.4s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.8rem;
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
        }

        .status-btn.accept {
          background: #e6f4ea;
          color: #155724;
        }

        .status-btn.accept.active {
          background: #28a745;
          color: white;
          transform: scale(1.05);
        }

        .status-btn.reject {
          background: #fde8e8;
          color: #721c24;
        }

        .status-btn.reject.active {
          background: #dc3545;
          color: white;
          transform: scale(1.05);
        }

        .status-btn:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }

        .reason-section {
          margin-bottom: 2rem;
        }

        .reason-section label {
          display: block;
          font-size: 1.15rem;
          font-weight: 700;
          color: #1e40af;
          margin-bottom: 0.8rem;
        }

        .required {
          color: #dc3545;
        }

        .reason-section textarea {
          width: 100%;
          padding: 1.2rem;
          border: 2px solid #e2e8f0;
          border-radius: 14px;
          font-size: 1.05rem;
          resize: vertical;
          transition: all 0.3s ease;
          background: #f8fafc;
        }

        .reason-section textarea:focus {
          outline: none;
          border-color: #3b82f6;
          box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.2);
        }

        .submit-btn {
          width: 100%;
          padding: 1.4rem;
          background: #1e40af;
          color: white;
          border: none;
          border-radius: 16px;
          font-size: 1.3rem;
          font-weight: 700;
          cursor: pointer;
          transition: all 0.4s ease;
          box-shadow: 0 10px 30px rgba(30, 64, 175, 0.4);
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.8rem;
        }

        .submit-btn:hover:not(:disabled) {
          background: #1e3a8a;
          transform: translateY(-4px);
        }

        .submit-btn:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }

        .back-btn {
          margin-top: 2rem;
          padding: 1rem 2rem;
          background: #64748b;
          color: white;
          border: none;
          border-radius: 14px;
          font-size: 1.1rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          width: 100%;
        }

        .back-btn.primary {
          background: #1e40af;
        }

        .back-btn:hover {
          background: #475569;
          transform: translateY(-3px);
        }

        .back-btn.primary:hover {
          background: #1e3a8a;
        }

        .decision-info p {
          font-size: 1.15rem;
          margin: 1.2rem 0;
          color: #334155;
        }

        .decision-info strong {
          color: #1e40af;
        }

        .status-badge {
          padding: 0.6rem 1.2rem;
          border-radius: 30px;
          font-weight: 700;
          font-size: 1rem;
          margin-left: 1rem;
        }

        .status-badge.guard-accepted {
          background: #d4edda;
          color: #155724;
        }

        .status-badge.guard-rejected {
          background: #f8d7da;
          color: #721c24;
        }

        .loading-state {
          text-align: center;
          padding: 4rem 2rem;
          color: #64748b;
        }

        .loading-state i {
          font-size: 3.5rem;
          color: #3b82f6;
          margin-bottom: 1.5rem;
        }

        /* Responsive */
        @media (max-width: 992px) {
          .decision-grid {
            grid-template-columns: 1fr;
          }

          .decision-header h1 {
            font-size: 2.2rem;
          }
        }

        @media (max-width: 768px) {
          .duty-decision-wrapper {
            padding: 1rem;
          }

          .decision-header {
            padding: 2rem 1.5rem;
            border-radius: 20px;
          }

          .officer-card,
          .decision-card {
            padding: 2rem;
          }

          .status-buttons {
            flex-direction: column;
          }

          .card-header h2 {
            font-size: 1.6rem;
          }
        }

        @media (max-width: 480px) {
          .decision-header h1 {
            font-size: 1.9rem;
            flex-direction: column;
          }
        }
      `}</style>
    </>
  );
}
// import React, { useEffect, useState } from "react";
// import { useNavigate } from "react-router-dom";
// import { useGuardStore } from "../context/GuardContext";
// import axios from "axios";
// import { toast } from "react-toastify";

// const BASE_URL = process.env.REACT_APP_BASE_URL;

// export default function GuardDutyDecision() {
//   const navigate = useNavigate();
//   const { selectedGuard } = useGuardStore();

//   const [guardId, setGuardId] = useState(null);
//   const [guardData, setGuardData] = useState(null);
//   const [submitting, setSubmitting] = useState(false);

//   const [decisionData, setDecisionData] = useState({
//     status: "",
//     reason: "",
//   });

//   const [existingDecision, setExistingDecision] = useState(null);
//   const [checkingDecision, setCheckingDecision] = useState(true);

//   /* ================= LOAD GUARD FROM CONTEXT OR LOCALSTORAGE ================= */
//   useEffect(() => {
//     let guard = selectedGuard;

//     if (!guard) {
//       const storedGuard = localStorage.getItem("selectedGuard");
//       if (storedGuard) {
//         guard = JSON.parse(storedGuard);
//       }
//     }

//     if (!guard) {
//       toast.error("Session expired. Please login again.");
//       navigate("/login");
//       return;
//     }

//     setGuardId(guard.id);
//     setGuardData(guard);
//   }, [selectedGuard, navigate]);

//   /* ================= FETCH EXISTING DECISION ================= */
//   useEffect(() => {
//     const fetchExistingDecision = async () => {
//       try {
//         const token = localStorage.getItem("guardToken");
//         if (!token) {
//           toast.error("Token expired. Please login again.");
//           navigate("/login");
//           return;
//         }

//         const res = await axios.get(`${BASE_URL}/api/duty/${guardId}`, {
//           headers: {
//             Authorization: `Bearer ${token}`,
//           },
//         });

//         if (res.data && res.data.length > 0) {
//           setExistingDecision(res.data[0]);
//         } else {
//           setExistingDecision(null);
//         }
//       } catch (err) {
//         console.error("Fetch Decision Error:", err);
//         setExistingDecision(null);
//       } finally {
//         setCheckingDecision(false);
//       }
//     };

//     if (guardId) fetchExistingDecision();
//   }, [guardId, navigate]);

//   /* ================= STATUS HANDLER ================= */
//   const handleStatusClick = (status) => {
//     setDecisionData({
//       status,
//       reason: "",
//     });
//   };

//   /* ================= REASON HANDLER ================= */
//   const handleReasonChange = (e) => {
//     setDecisionData((prev) => ({
//       ...prev,
//       reason: e.target.value,
//     }));
//   };

//   /* ================= SUBMIT ================= */
//   const handleSubmit = async (e) => {
//     e.preventDefault();
//     if (submitting) return;

//     if (!decisionData.status) {
//       toast.error("Please select Accept or Reject");
//       return;
//     }

//     if (
//       decisionData.status === "Guard Rejected" &&
//       !decisionData.reason.trim()
//     ) {
//       toast.error("Reason is required for rejection");
//       return;
//     }

//     const dutyPayload = {
//       officer: guardData,
//       status: decisionData.status,
//       reason: decisionData.reason,
//       message: decisionData.reason,
//     };

//     try {
//       setSubmitting(true);

//       const token = localStorage.getItem("guardToken");
//       if (!token) {
//         toast.error("Token expired. Please login again.");
//         navigate("/login");
//         return;
//       }

//       await axios.post(`${BASE_URL}/api/duty/decision`, dutyPayload, {
//         headers: {
//           Authorization: `Bearer ${token}`,
//         },
//       });

//       toast.success("Duty decision submitted successfully");
//       setDecisionData({ status: "", reason: "" });
//       navigate("/guardshift");
//     } catch (err) {
//       console.error("Duty Submit Error:", err);
//       toast.error("Failed to submit duty decision");
//     } finally {
//       setSubmitting(false);
//     }
//   };

//   /* ================= UI ================= */
//   return (
//     <div style={styles.page}>
//       <div style={styles.container}>
//         {/* LEFT SIDE */}
//         <div style={styles.leftBox}>
//           <h2 style={styles.leftTitle}>DUTY INFORMATION</h2>
//           <p style={styles.desc}>Accept or Reject your assigned duty.</p>

//           <p><b>Officer ID:</b> {guardId}</p>
//           <p><b>Officer Name:</b> {guardData?.name}</p>

//           <button
//             style={styles.haveBtn}
//             onClick={() => navigate("/guardshift")}
//           >
//             Back to Shift
//           </button>
//         </div>

//         {/* RIGHT SIDE */}
//         <div style={styles.formBox}>
//           {checkingDecision ? (
//             <h3>Checking previous decision...</h3>
//           ) : existingDecision ? (
//             <>
//               <h2 style={styles.formTitle}>Duty Status</h2>

//               <p><b>Status:</b> {existingDecision.status}</p>
//               <p><b>Message:</b> {existingDecision.message}</p>

//               <p><b>Officer:</b> {existingDecision.officer?.name}</p>
//               <p><b>Rank:</b> {existingDecision.officer?.rank}</p>

//               <button
//                 style={{ ...styles.submitBtn, marginTop: 30 }}
//                 onClick={() => navigate("/guardshift")}
//               >
//                 Back to Shift
//               </button>
//             </>
//           ) : (
//             <>
//               <h2 style={styles.formTitle}>Duty Acceptance</h2>

//               <form onSubmit={handleSubmit}>
//                 <div style={{ display: "flex", gap: 15, marginBottom: 20 }}>
//                   <button
//                     type="button"
//                     disabled={submitting}
//                     onClick={() => handleStatusClick("Guard Accepted")}
//                     style={{
//                       ...styles.actionBtn,
//                       background:
//                         decisionData.status === "Guard Accepted"
//                           ? "#28a745"
//                           : "#e0e0e0",
//                       color:
//                         decisionData.status === "Guard Accepted"
//                           ? "white"
//                           : "black",
//                     }}
//                   >
//                     Accept Duty
//                   </button>

//                   <button
//                     type="button"
//                     disabled={submitting}
//                     onClick={() => handleStatusClick("Guard Rejected")}
//                     style={{
//                       ...styles.actionBtn,
//                       background:
//                         decisionData.status === "Guard Rejected"
//                           ? "#dc3545"
//                           : "#e0e0e0",
//                       color:
//                         decisionData.status === "Guard Rejected"
//                           ? "white"
//                           : "black",
//                     }}
//                   >
//                     Reject Duty
//                   </button>
//                 </div>

//                 {decisionData.status === "Guard Rejected" && (
//                   <div style={{ marginBottom: 20 }}>
//                     <label><b>Reason for Rejection</b></label>
//                     <textarea
//                       value={decisionData.reason}
//                       onChange={handleReasonChange}
//                       rows="3"
//                       style={{
//                         width: "100%",
//                         padding: "10px",
//                         borderRadius: "5px",
//                         border: "1px solid #ccc",
//                         marginTop: "6px",
//                       }}
//                       placeholder="Enter reason for rejection..."
//                     />
//                   </div>
//                 )}

//                 <button
//                   type="submit"
//                   disabled={submitting}
//                   style={styles.submitBtn}
//                 >
//                   {submitting ? "Submitting..." : "Submit Duty Decision"}
//                 </button>
//               </form>
//             </>
//           )}
//         </div>
//       </div>
//     </div>
//   );
// }

// /* ================= STYLES ================= */
// const styles = {
//   page: {
//     minHeight: "90vh",
//     background: "#f4f6fa",
//     display: "flex",
//     justifyContent: "center",
//     alignItems: "center",
//   },
//   container: {
//     width: "100%",
//     minHeight: "80vh",
//     display: "flex",
//     flexWrap: "wrap",
//     background: "white",
//   },
//   leftBox: {
//     width: "40%",
//     minWidth: "300px",
//     padding: "40px",
//   },
//   leftTitle: {
//     fontSize: "28px",
//     fontWeight: "700",
//     marginBottom: "20px",
//   },
//   desc: {
//     fontSize: "15px",
//     marginBottom: "10px",
//   },
//   haveBtn: {
//     background: "gray",
//     color: "white",
//     border: "none",
//     padding: "12px 25px",
//     borderRadius: "5px",
//     cursor: "pointer",
//     marginTop: "30px",
//   },
//   formBox: {
//     width: "60%",
//     minWidth: "300px",
//     padding: "40px",
//   },
//   formTitle: {
//     fontSize: "26px",
//     fontWeight: "700",
//     color: "#1e73be",
//     marginBottom: "20px",
//   },
//   submitBtn: {
//     color: "white",
//     border: "none",
//     padding: "12px",
//     borderRadius: "5px",
//     width: "100%",
//     cursor: "pointer",
//     background: "#1e73be",
//   },
//   actionBtn: {
//     flex: 1,
//     border: "none",
//     padding: "12px",
//     borderRadius: "5px",
//     cursor: "pointer",
//     fontWeight: "600",
//   },
// };
